import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import exportExcel from "utils/excelGenerate";
import { getDealerProspectus, getTasksDetails } from '_controllers/officer';
import { PageHeader, StatCard, SectionCard, EmptyState } from "_shared/ui";

const Leads = () => {
  const { SAP_ID } = useParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [visitDetails, setVisitDetails] = useState([])
  const [message, setMessage] = useState("");
  const [onDate, setOnDate] = useState('');
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [csvData, setCsvData] = useState([]);

  const [Values, setValues] = useState({
    selectStartDate: '',
    selectEndDate: '',
  });

  useEffect(async () => {
    const start = startDate
      ? startDate
      : moment().startOf("month").format("YYYY-MM-DD");

    const end = endDate
      ? endDate
      : moment().endOf("month").format("YYYY-MM-DD");
    const plan = await getDealerProspectus({
      SAP_ID,
      start,
      end,
    });
    console.log("lead", plan.data);
    setData(plan?.data);
    setCsvData(plan?.data)
    setLoading(true);
  }, [message]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    setValues({ ...Values, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const plan = await getDealerProspectus({
      SAP_ID,
      start: selectStartDate,
      end: selectEndDate,
    });
    setData(plan?.data);
    setCsvData(plan?.data)
    setLoading(true);
  }

  const { selectStartDate, selectEndDate } = Values
  const fileName = `${SAP_ID}-Tasks`;

  const generateExcel = async (e) => {
    e.preventDefault()

    exportExcel(csvData.map((x) => {
      return {
        'SAP ID': x?.SAP_ID,
        "Dealer Code": x.dealer.dealerCode,
        'Dealer Name': x.dealer.name,
        'Type': x.type,
        "Subject": x?.subject ? x?.subject : "-",
        'Status': x?.status,
        "Due Date": moment.utc(x?.dueDate).format("DD-MM-YYYY"),
        "Remarks": x?.description,
      }
    }), fileName)
  }

  // KPIs derived directly from the loaded prospectus records (no fabricated data).
  const totalLeads = data?.length ?? 0;
  const withPhone = data?.filter((d) => d?.basicDetail?.phoneNumber)?.length ?? 0;
  const withDocuments = data?.filter((d) => d?.description)?.length ?? 0;

  return (
    <Box sx={{ mt: 2 }}>
      <PageHeader title="Leads" subtitle="Dealer prospectus and lead records" />

      {loading && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Total Leads"
              value={totalLeads}
              icon={GroupsOutlinedIcon}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="With Phone"
              value={withPhone}
              icon={PhoneOutlinedIcon}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="With Documents"
              value={withDocuments}
              icon={DescriptionOutlinedIcon}
              color="success"
            />
          </Grid>
        </Grid>
      )}

      <SectionCard sx={{ mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <TextField
            type="date"
            label="Start Date"
            InputLabelProps={{ shrink: true }}
            onChange={handleChange}
            value={selectStartDate}
            name="selectStartDate"
            sx={{ minWidth: 200 }}
          />
          <TextField
            type="date"
            label="End Date"
            InputLabelProps={{ shrink: true }}
            onChange={handleChange}
            value={selectEndDate}
            name="selectEndDate"
            sx={{ minWidth: 200 }}
          />
          <Box sx={{ display: 'flex', gap: 1, ml: { sm: 'auto' } }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<FilterAltOutlinedIcon />}
            >
              Filter
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={generateExcel}
            >
              Download
            </Button>
          </Box>
        </Box>
      </SectionCard>

      {loading ? (
        <SectionCard title="Prospectus" disablePadding>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">Firm Name</TableCell>
                  <TableCell align="left">Firm Nature</TableCell>
                  <TableCell align="left">Address</TableCell>
                  <TableCell align="left">Phone</TableCell>
                  <TableCell align="left">Lead Type</TableCell>
                  <TableCell align="left">Business Nature</TableCell>
                  <TableCell align="left">Prospect Type</TableCell>
                  <TableCell align="left">Document</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.length > 0 ? (
                  data?.map((d) => (
                    <TableRow key={d._id}>
                      <TableCell align="left">{d?.basicDetail?.firmName}</TableCell>
                      <TableCell align="left">{d?.basicDetail?.natureOfFirm}</TableCell>
                      <TableCell align="left">{d?.basicDetail?.address1},{d?.basicDetail?.state?.name}</TableCell>
                      <TableCell align="left">{d?.basicDetail?.phoneNumber}</TableCell>
                      <TableCell align="left">{d?.basicDetail?.leadType}</TableCell>
                      <TableCell align="left">{d?.businessDetail?.natureOfBusiness}</TableCell>
                      <TableCell align="left">{d?.businessDetail?.prospectType}</TableCell>
                      <TableCell align="left">{d?.description}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ p: 0 }}>
                      <EmptyState
                        title="No Prospectus Available"
                        description="There are no leads for the selected period."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  )
}

export default Leads